import { IfAbility } from '@efdigital/ll-addon-auth';
import type { Cohort } from '@efdigital/ll-addon-data/types/xceed/cohort';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { CohortListing } from '@efdigital/ll-admin-v2/components/programs/cohort-listing.gts';
import { EditCohort } from '@efdigital/ll-admin-v2/components/programs/edit-cohort';
import { findCohort } from '@efdigital/ll-admin-v2/data/builders/xceed/cohorts';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type ProgramsProgramCohortRoute from '@efdigital/ll-admin-v2/routes/programs/program/cohort';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { cacheKeyFor } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import { serializePatch } from '@warp-drive/utilities/json-api';
import { updateRecord } from '@warp-drive/utilities/json-api';
import { pageTitle } from 'ember-page-title';

interface IndexSignature {
  Args: {
    model: ModelFrom<ProgramsProgramCohortRoute>;
    controller: unknown;
  };
}

export default class ProgramsProgramCohortIndexTemplate extends Component<IndexSignature> {
  @service declare store: Store;

  updateCohort = (currentCohort: Cohort, updates: Partial<Cohort>) => {
    // Filter out empty string values and convert dates to ISO format
    const cleanedUpdates: Partial<Cohort> = {};
    for (const [key, value] of Object.entries(updates)) {
      // Skip empty strings for date fields
      if ((key === 'starts' || key === 'ends') && value === '') {
        continue;
      }
      // Convert date strings to ISO format
      if (
        (key === 'starts' || key === 'ends') &&
        value &&
        typeof value === 'string'
      ) {
        // If it's in YYYY-MM-DD format, convert to ISO datetime
        // Add 'T00:00:00Z' to prevent timezone shift when converting to ISO
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          cleanedUpdates[key as keyof Cohort] = new Date(
            value + 'T00:00:00Z'
          ).toISOString() as never;
          continue;
        }
      }
      cleanedUpdates[key as keyof Cohort] = value as never;
    }

    Object.assign(currentCohort, cleanedUpdates);

    const init = updateRecord(currentCohort, {
      ...jsonApiOptions,
      patch: true,
    });
    init.body = JSON.stringify(
      serializePatch(this.store.cache, cacheKeyFor(currentCohort))
    );
    init.headers.set('Content-Type', 'application/vnd.api+json');

    return this.store.request(init);
  };

  <template>
    <div class="container py-5">
      {{pageTitle "Index"}}
      <IfAbility @ability="cohorts" @atLeast="viewer">
        <:if>
          <Request @query={{findCohort @model.cohort_id}}>
            <:content as |content|>
              <IfAbility @ability="cohorts" @atLeast="editor">
                <:if>
                  <EditContext as |context|>
                    <div class="row g-5">
                      <div class="col-md-8">
                        <EditCohort
                          @item={{content.data}}
                          @context={{context}}
                        />
                      </div>
                      <div class="col-md-4">
                        <h3 class="gud-eyebrow">Preview</h3>
                        <CohortListing @cohort={{content.data}} />
                      </div>
                    </div>
                  </EditContext>
                </:if>
                <:else>
                  <div class="row g-5">
                    <div class="col-12">
                      <h3 class="gud-eyebrow">Preview</h3>
                      <CohortListing @cohort={{content.data}} />
                      <div class="alert alert-info mt-4" role="alert">
                        <h4 class="alert-heading">View Only</h4>
                        <p>You have view-only access to cohorts.</p>
                        <p class="mb-0">To edit cohorts, you need the "Edit
                          cohorts" permission.</p>
                      </div>
                    </div>
                  </div>
                </:else>
              </IfAbility>
            </:content>
          </Request>
        </:if>
        <:else>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view cohorts.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:else>
      </IfAbility>
    </div>
  </template>
}
